/**
 * VH-260: the button that puts a recipe's ingredients on the shopping list.
 *
 * Reto, 2026-09-24, after testing VH-253 on his phone: "What is missing that I can push
 * a button in the recipe and add the ingredients to the shopping list." Pasting the
 * recipe's text already worked — but the app wrote the recipe, so asking him to copy it
 * back in is exactly the fiddling the shopping list exists to remove.
 *
 * VH-274: it sits with the recipe's other actions at the top and looks like them. Beside
 * the ingredients, as an outline button a screen and a half down, it was not found.
 *
 * One tap, no dialog. The result is stated rather than assumed ("6 added, 2 already on
 * the list"), because "already on the list" is the common case once a second recipe
 * needs salt, and a silent success there looks like a button that did nothing.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ShoppingCartIcon } from '@heroicons/react/24/outline'

import { useAddFromRecipe } from '../../hooks/useShoppingList'
import { errorCode } from '../../services/shopping'

export default function AddToShoppingListButton({ recipeId, versionId, disabled = false }) {
  const { t } = useTranslation()
  const addFromRecipe = useAddFromRecipe()
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  // A new recipe (or a new version of it) is a new question — an answer about the
  // previous one must not stay on screen next to it.
  useEffect(() => {
    setResult(null)
    setError(null)
  }, [recipeId, versionId])

  const handleClick = async () => {
    setResult(null)
    setError(null)
    try {
      setResult(await addFromRecipe.mutateAsync({ recipeId, versionId }))
    } catch (err) {
      setError(errorCode(err) || 'failed')
    }
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || addFromRecipe.isPending}
        className="btn-primary flex items-center gap-1 text-sm disabled:opacity-50"
        data-testid="add-to-shopping-list-btn"
      >
        <ShoppingCartIcon className="h-4 w-4" aria-hidden="true" />
        {addFromRecipe.isPending
          ? t('recipes.detail.addingToShoppingList')
          : t('recipes.detail.addToShoppingList')}
      </button>

      {result && (
        <p className="text-sm text-green-700">
          {t('recipes.detail.addedToShoppingList', {
            added: result.added_count,
            merged: result.merged_count,
          })}{' '}
          <Link to="/shopping" className="underline">
            {t('recipes.detail.openShoppingList')}
          </Link>
        </p>
      )}

      {error && (
        <p className="text-sm text-red-600">
          {t([`recipes.detail.shoppingErrors.${error}`, 'recipes.detail.shoppingErrors.failed'])}
        </p>
      )}
    </div>
  )
}
